import { uiText, useUiLanguage, currentLocale, statusText } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageSquare, Clock, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface SupportRequest {
  id: string;
  subject: string;
  message: string;
  priority: string;
  status: string;
  created_at: string;
  response: string | null;
  resolved_at: string | null;
}

interface SupportRequestStatusProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SupportRequestStatus: React.FC<SupportRequestStatusProps> = ({
  open,
  onOpenChange
}) => {
  useUiLanguage();
  const { t } = useTranslation(['admin']);
  const { user } = useAuth();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  useEffect(() => {
    if (open && user) {
      fetchSupportRequests();
      setupRealtimeSubscription();
    }
  }, [open, user]);

  const fetchSupportRequests = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_requests')
        .select('*')
        .eq('admin_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error) {
      console.error('Error fetching support requests:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to fetch support requests"),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel('support_requests_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'support_requests',
          filter: `admin_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Support request updated:', payload);

          // Update the local state with the new data
          setRequests(prev => prev.map(req =>
            req.id === payload.new.id ? payload.new as SupportRequest : req
          ));

          // Show toast notification if there's a new response
          if (payload.new.response && !payload.old.response) {
            toast({
              title: uiText("New Response"),
              description: uiText("You have received a response to your support request: \"{{value0}}\"", { value0: payload.new.subject }),
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <Clock className="h-4 w-4" />;
      case 'replied':
        return <MessageSquare className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'open': return 'default';
      case 'replied': return 'secondary';
      case 'resolved': return 'outline';
      default: return 'outline';
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'normal': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const openDetailsDialog = (request: SupportRequest) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {uiText("My Support Requests")}</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">{uiText("Loading your support requests...")}</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{uiText("No support requests found")}</p>
              <p className="text-sm">{uiText("Submit a support request to get help from our team")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{uiText("Subject")}</TableHead>
                    <TableHead>{uiText("Priority")}</TableHead>
                    <TableHead>{uiText("Status")}</TableHead>
                    <TableHead>{uiText("Created")}</TableHead>
                    <TableHead>{uiText("Response")}</TableHead>
                    <TableHead>{uiText("Actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium max-w-xs">
                        <div className="truncate" title={request.subject}>
                          {request.subject}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(request.priority)}>
                          {request.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(request.status)}
                          <Badge variant={getStatusBadgeVariant(request.status)}>
                            {statusText(request.status)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(request.created_at).toLocaleDateString(currentLocale())}
                      </TableCell>
                      <TableCell>
                        {request.response ? (
                          <Badge variant="secondary">
                            {uiText("Response Available")}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">{uiText("Waiting")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetailsDialog(request)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          {uiText("View")}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Request Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{uiText("Support Request Details")}</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Info */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">{selectedRequest.subject}</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant={getPriorityBadgeVariant(selectedRequest.priority)}>
                        {selectedRequest.priority}
                      </Badge>
                      <Badge variant={getStatusBadgeVariant(selectedRequest.status)}>
                        {statusText(selectedRequest.status)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{uiText("Submitted on:")}</p>
                      <p className="text-sm">{new Date(selectedRequest.created_at).toLocaleString(currentLocale())}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{uiText("Your message:")}</p>
                      <p className="whitespace-pre-wrap bg-muted p-3 rounded text-sm">
                        {selectedRequest.message}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Response */}
              {selectedRequest.response ? (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      <h4 className="font-semibold">{uiText("Response from Support Team")}</h4>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedRequest.resolved_at && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">{uiText("Responded on:")}</p>
                          <p className="text-sm">{new Date(selectedRequest.resolved_at).toLocaleString(currentLocale())}</p>
                        </div>
                      )}
                      <div>
                        <p className="whitespace-pre-wrap bg-green-50 border-l-4 border-green-400 p-3 rounded text-sm">
                          {selectedRequest.response}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">{uiText("Waiting for response...")}</p>
                    <p className="text-sm text-muted-foreground">{uiText("Our support team will review and respond to your request soon.")}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportRequestStatus;